import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import AddIcon from '@mui/icons-material/Add';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import EventIcon from '@mui/icons-material/Event';
import GroupIcon from '@mui/icons-material/Group';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { carUrl, eventUrl } from '../lib/slugId';
import { LogCard } from '../components/LogCard';
import { useThemeMode } from '../theme/ThemeModeContext';
import type { Car, Event as EventModel, Paginated, SingleResponse, SiteStats } from '../api/types';
import { CarCardMedia } from '../components/CarCardMedia';

export function Landing() {
  const { t } = useTranslation();
  const { resolvedMode } = useThemeMode();
  const isDark = resolvedMode === 'dark';
  const [highlights, setHighlights] = useState<Car[]>([]);
  const [recentCars, setRecentCars] = useState<Car[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<EventModel[]>([]);
  const [stats, setStats] = useState<SiteStats | null>(null);

  useEffect(() => {
    const nowIso = new Date().toISOString();
    apiFetch<Paginated<Car>>('/api/v1/cars?per_page=3')
      .then((res) => setHighlights(res.data))
      .catch(() => setHighlights([]));
    apiFetch<Paginated<Car>>('/api/v1/cars?sort=-created_at&per_page=4&include=user')
      .then((res) => setRecentCars(res.data))
      .catch(() => setRecentCars([]));
    apiFetch<Paginated<EventModel>>(
      `/api/v1/events?filter[starts_after]=${encodeURIComponent(nowIso)}&sort=starts_at&include=organizer&per_page=4`
    )
      .then((res) => setUpcomingEvents(res.data))
      .catch(() => setUpcomingEvents([]));
    apiFetch<SingleResponse<SiteStats>>('/api/v1/stats')
      .then((res) => setStats(res.data))
      .catch(() => setStats(null));
  }, []);

  return (
    <Box>
      {/* Hero — theme-aware: dark image + white text in dark mode, light image + dark text in light mode */}
      <Box
        sx={{
          color: isDark ? 'common.white' : 'common.black',
          backgroundColor: isDark ? '#0E0F11' : '#FAFAFA',
          position: 'relative',
          overflow: 'hidden',
          minHeight: { xs: 480, md: 560 },
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          mb: { xs: 6, md: 8 },
        }}
      >
        {/* Image at full height (contain); fade is baked into the image itself */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${isDark ? '/hero_dark.jpg' : '/hero_light.jpg'})`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
        {/* Overlay for text legibility on the left — dark overlay in dark mode, light overlay in light mode */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: isDark
              ? 'linear-gradient(90deg, #0E0F11 0%, rgba(10, 11, 13, 0.7) 25%, rgba(10, 11, 13, 0.15) 55%, transparent 100%)'
              : 'linear-gradient(90deg, #FAFAFA 0%, rgba(250, 250, 250, 0.8) 25%, rgba(250, 250, 250, 0.2) 55%, transparent 100%)',
          }}
        />
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 }, position: 'relative', zIndex: 1 }}>
          <Stack spacing={4} sx={{ maxWidth: { xs: '100%', md: '60%' } }}>
            <Typography variant="h1">{t('hero.tagline')}</Typography>
            <Typography
              variant="h6"
              sx={{
                color: isDark ? 'rgba(255, 255, 255, 0.75)' : 'rgba(15, 17, 21, 0.75)',
                fontWeight: 400,
                maxWidth: 560,
              }}
            >
              {t('hero.subtitle')}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                component={RouterLink}
                to="/cars/new"
              >
                {t('hero.addYourCar')}
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={RouterLink}
                to="/explore"
                sx={{
                  color: isDark ? 'common.white' : 'common.black',
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(15, 17, 21, 0.4)',
                  '&:hover': {
                    borderColor: isDark ? 'common.white' : 'common.black',
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 17, 21, 0.04)',
                  },
                }}
              >
                {t('hero.browseBuilds')}
              </Button>
            </Stack>

            {stats && (
              <Stack
                direction="row"
                spacing={{ xs: 3, sm: 5 }}
                sx={{ pt: 2, flexWrap: 'wrap' }}
                useFlexGap
              >
                <HeroStat icon={<DirectionsCarIcon />} value={stats.cars} label={t('hero.cars')} isDark={isDark} />
                <HeroStat icon={<EventIcon />} value={stats.events} label={t('hero.events')} isDark={isDark} />
                <HeroStat icon={<GroupIcon />} value={stats.members} label={t('hero.members')} isDark={isDark} />
              </Stack>
            )}
          </Stack>
        </Container>
      </Box>

      {/* Today's highlights */}
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Typography variant="h4">{t('landing.todaysHighlights')}</Typography>
          <Grid container spacing={3}>
            {highlights.map((car) => (
              <Grid key={car.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card>
                  <CardActionArea component={RouterLink} to={carUrl(car)}>
                    <CarCardMedia car={car} height={200} />
                    <CardContent>
                      <Typography variant="h6">{car.display_name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {car.year} {car.make} {car.model}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Container>

      {/* Recent additions + Upcoming events */}
      <Container maxWidth="lg" sx={{ pt: { xs: 6, md: 8 }, pb: { xs: 6, md: 8 } }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Stack spacing={2} sx={{ height: '100%' }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ flexWrap: 'wrap', gap: 1 }}
              >
                <Typography variant="h4">{t('dashboard_sections.recentlyAdded')}</Typography>
                <Button component={RouterLink} to="/explore" size="small">
                  {t('dashboard_sections.viewAll')}
                </Button>
              </Stack>
              <Grid container spacing={2}>
                {recentCars.map((car) => (
                  <Grid key={car.id} size={{ xs: 12, sm: 6 }}>
                    <Card>
                      <CardActionArea component={RouterLink} to={carUrl(car)}>
                        <CarCardMedia car={car} height={160} />
                        <CardContent>
                          <Typography variant="h6">{car.display_name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {car.year} {car.make} {car.model}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={2} sx={{ height: '100%' }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ flexWrap: 'wrap', gap: 1 }}
              >
                <Typography variant="h4">{t('dashboard_sections.upcomingEvents')}</Typography>
                <Button component={RouterLink} to="/events" size="small">
                  {t('dashboard_sections.viewAll')}
                </Button>
              </Stack>
              {upcomingEvents.length === 0 ? (
                <Typography color="text.secondary">{t('dashboard_sections.noUpcoming')}</Typography>
              ) : (
                <Stack spacing={2}>
                  {upcomingEvents.map((evt) => (
                    <LogCard
                      key={evt.id}
                      date={evt.starts_at}
                      title={evt.title}
                      location={`${evt.location_name}${evt.city ? ', ' + evt.city : ''}`}
                      to={eventUrl(evt)}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

function HeroStat({
  icon,
  value,
  label,
  isDark,
}: {
  icon: React.ReactNode;
  value: number;
  label: string;
  isDark: boolean;
}) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box sx={{ color: 'primary.light', display: 'flex' }}>{icon}</Box>
      <Stack spacing={0}>
        <Typography
          variant="h5"
          sx={{ color: isDark ? 'common.white' : 'common.black', lineHeight: 1 }}
        >
          {value.toLocaleString()}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(15, 17, 21, 0.6)' }}
        >
          {label}
        </Typography>
      </Stack>
    </Stack>
  );
}
